/**
 * Marketing pricing catalog — shared by /pricing and its section pages.
 * Amounts match backend PRODUCT_PRICES_CENTS / PRODUCT_DISPLAY_NAMES.
 */

export type PriceLineItem = {
  name: string;
  /** In-page anchor for dropdown deep links. */
  anchorId?: string;
  price: number | null;
  priceNote?: string;
  description: string;
  /** Checkout product when this line is buyable before upload. */
  productType?: string;
  analysesCount?: number;
};

export type PriceTier = {
  name: string;
  description: string;
  price: number;
  perUnit: number | null;
  badge: string | null;
  features: string[];
  productType: string;
  analysesCount: number;
};

export function priceWhole(price: number | null): number {
  if (price === null) return 0;
  return Math.floor(price);
}

export function priceCents(price: number | null): string {
  if (price === null) return '00';
  return String(Math.round((price % 1) * 100)).padStart(2, '0');
}

export const FEATURE_FILM_FORMATS: PriceLineItem[] = [
  {
    name: "Writer's Coverage",
    anchorId: 'writers-coverage',
    price: 19.99,
    description:
      'Writer-feedback-only coverage — story, structure, character, dialogue, and originality, without producer or executive analysis.',
  },
  {
    name: 'Treatment',
    anchorId: 'treatment',
    price: 9.99,
    description: 'Feature film treatment analysis with story arc, market positioning, and viability assessment.',
  },
  {
    name: 'Outline',
    anchorId: 'outline',
    price: 9.99,
    description: 'Beat-sheet and outline review for early-stage development and revision planning.',
  },
  {
    name: 'Short Film Screenplay',
    anchorId: 'short-film',
    price: null,
    priceNote: 'Free',
    description: 'Full Quilty analysis for short-form screenplays at no charge.',
  },
];

export const TV_FORMATS: PriceLineItem[] = [
  {
    name: 'TV Series — Pilot, bible, or season',
    anchorId: 'television',
    price: 39.99,
    description:
      'Unified TV coverage: a pilot, a pilot + bible, or a full season of any episode count.',
  },
  {
    name: 'TV Pitch',
    anchorId: 'tv-pitch',
    price: 19.99,
    description: 'Pitch document and series concept evaluation before you invest in a full bible or pilot draft.',
  },
];

export const STAGE_PLAY: PriceLineItem = {
  name: 'Stage Play — Theater Intelligence',
  price: 29.99,
  description:
    'Theater-specific analysis: production constraints, audience dynamics, and market tiers from regional to Broadway.',
  productType: 'stage_play',
  analysesCount: 1,
};

export const STAGE_PLAY_FEATURES = [
  'Production & staging constraints',
  'Audience and market tier fit',
  'Regional, Off-Broadway & Broadway positioning',
  'Theater-specific Quilty Score',
] as const;

export const ADAPTATION_AND_DECKS: PriceLineItem[] = [
  {
    name: 'Novel Adaptation',
    anchorId: 'novel-adaptation',
    price: 49.99,
    description:
      'Book-to-screen adaptation intelligence — source material strengths, adaptation strategy, and market angle.',
    productType: 'novel_adaptation',
    analysesCount: 1,
  },
  {
    name: 'Pitch Deck Analysis',
    anchorId: 'pitch-deck',
    price: 9.99,
    description: 'Investor and buyer deck review. One deck included free with each full screenplay analysis you purchase.',
    productType: 'pitch_deck',
    analysesCount: 1,
  },
];

export const SPECIALIZED_ADDONS: PriceLineItem[] = [
  {
    name: 'Territorial Censorship Analysis',
    anchorId: 'territorial-censorship',
    price: 15.99,
    description: 'Compliance and censorship risk by territory for international distribution planning.',
  },
];

export const AUTHENTICITY_ADDONS: PriceLineItem[] = [
  {
    name: 'Feature Film or TV Pilot',
    anchorId: 'authenticity-feature-or-pilot',
    price: 5.99,
    description: 'AI authenticity screening add-on after your full analysis.',
  },
  {
    name: 'TV Episode',
    anchorId: 'authenticity-tv-episode',
    price: 3.99,
    priceNote: 'per episode',
    description: 'Per-episode authenticity check for serialized TV.',
  },
  {
    name: 'Treatment, Pitch, or Deck',
    anchorId: 'authenticity-treatment-pitch-deck',
    price: 2.99,
    description: 'Lightweight authenticity pass for supporting documents.',
  },
  {
    name: 'Novel',
    anchorId: 'authenticity-novel',
    price: 9.99,
    description: 'Authenticity screening for novel and adaptation source material.',
  },
  {
    name: 'Stage Play',
    anchorId: 'authenticity-stage-play',
    price: 4.99,
    description: 'Authenticity screening for theater manuscripts.',
  },
  {
    name: 'Private Mode',
    anchorId: 'authenticity-private-mode',
    price: 2.0,
    priceNote: 'add-on',
    description: 'Keep authenticity results off shared reports (+$2.00 on top of the check above).',
  },
];

export const FEATURE_FILM_TIERS: PriceTier[] = [
  {
    name: 'Individual',
    description: 'Full professional coverage for a single feature screenplay.',
    price: 49.99,
    perUnit: null,
    badge: null,
    productType: 'feature_film_screenplay',
    analysesCount: 1,
    features: [
      'Complete logline & synopsis',
      'Character breakdown & analysis',
      'Structure & pacing evaluation',
      'Market & genre positioning',
      'Industry-grade scoring',
      'Delivered in under an hour',
    ],
  },
  {
    name: '3-Analysis Pack',
    description: 'Coverage for three feature screenplays at a discounted rate.',
    price: 134.99,
    perUnit: 45.0,
    badge: null,
    productType: 'feature_film_screenplay',
    analysesCount: 3,
    features: [
      'Everything in Individual',
      '3 full coverage reports',
      'Credits apply to this analysis type — use anytime, no expiration',
      'Revision-friendly workflow',
      'Priority email support',
      'Save 10% per analysis',
    ],
  },
  {
    name: '5-Analysis Pack',
    description: 'Our best value for serious writers with multiple projects.',
    price: 199.99,
    perUnit: 40.0,
    badge: 'Best Value',
    productType: 'feature_film_screenplay',
    analysesCount: 5,
    features: [
      'Everything in 3-Pack',
      '5 full coverage reports',
      'Credits apply to this analysis type — use anytime, no expiration',
      'Revision-friendly workflow',
      'Priority email support',
      'Save 20% per analysis',
    ],
  },
];

export const TV_PILOT_TIERS: PriceTier[] = [
  {
    name: 'Individual',
    description: 'Full professional coverage for a single TV pilot & bible.',
    price: 39.99,
    perUnit: null,
    badge: null,
    productType: 'tv_series',
    analysesCount: 1,
    features: [
      'Pilot script analysis',
      'Bible & series arc evaluation',
      'Character & world-building review',
      'Tone & audience assessment',
      'Industry-grade scoring',
      'Delivered in under an hour',
    ],
  },
  {
    name: '3-Analysis Pack',
    description: 'Coverage for three TV pilots at a discounted rate.',
    price: 107.99,
    perUnit: 36.0,
    badge: null,
    productType: 'tv_series',
    analysesCount: 3,
    features: [
      'Everything in Individual',
      '3 full coverage reports',
      'Credits apply to this analysis type — use anytime, no expiration',
      'Revision-friendly workflow',
      'Priority email support',
      'Save 10% per analysis',
    ],
  },
  {
    name: '5-Analysis Pack',
    description: 'Our best value for showrunners and staffing-season prep.',
    price: 159.99,
    perUnit: 32.0,
    badge: 'Best Value',
    productType: 'tv_series',
    analysesCount: 5,
    features: [
      'Everything in 3-Pack',
      '5 full coverage reports',
      'Credits apply to this analysis type — use anytime, no expiration',
      'Revision-friendly workflow',
      'Priority email support',
      'Save 20% per analysis',
    ],
  },
];

export const ADDITIONAL_OFFERINGS: PriceLineItem[] = [
  ...ADAPTATION_AND_DECKS,
  ...SPECIALIZED_ADDONS,
];

/** Old /pricing#anchor bookmarks → section pages (hash is client-only). */
export const LEGACY_PRICING_HASH_REDIRECTS: Record<string, string> = {
  'feature-film': '/pricing/feature-film',
  'writers-coverage': '/pricing/feature-film#writers-coverage',
  treatment: '/pricing/feature-film#treatment',
  outline: '/pricing/feature-film#outline',
  'short-film': '/pricing/feature-film#short-film',
  television: '/pricing/television',
  'tv-series-4-6': '/pricing/television#tv-series-4-6',
  'tv-series-7-12': '/pricing/television#tv-series-7-12',
  'tv-pitch': '/pricing/television#tv-pitch',
  theater: '/pricing/theater',
  'additional-offerings': '/pricing/additional-offerings',
  'novel-adaptation': '/pricing/additional-offerings#novel-adaptation',
  'pitch-deck': '/pricing/additional-offerings#pitch-deck',
  'territorial-censorship': '/pricing/additional-offerings#territorial-censorship',
  authenticity: '/pricing/authenticity',
  'authenticity-feature-or-pilot': '/pricing/authenticity#authenticity-feature-or-pilot',
  'authenticity-tv-episode': '/pricing/authenticity#authenticity-tv-episode',
  'authenticity-treatment-pitch-deck': '/pricing/authenticity#authenticity-treatment-pitch-deck',
  'authenticity-novel': '/pricing/authenticity#authenticity-novel',
  'authenticity-stage-play': '/pricing/authenticity#authenticity-stage-play',
  'authenticity-private-mode': '/pricing/authenticity#authenticity-private-mode',
};

export type PricingHubCard = {
  href: string;
  /** svelte-i18n prefix under pricingHub.cards.* */
  i18nKey: string;
  kicker: string;
  title: string;
  blurb: string;
  fromLabel: string;
};

export const PRICING_HUB_CARDS: PricingHubCard[] = [
  {
    href: '/pricing/feature-film',
    i18nKey: 'pricingHub.cards.featureFilm',
    kicker: 'Feature Films',
    title: 'Screenplay Coverage',
    blurb: 'Full-length feature analysis, plus writer\'s coverage, treatments, outlines, and free short films.',
    fromLabel: '$49.99 full analysis',
  },
  {
    href: '/pricing/television',
    i18nKey: 'pricingHub.cards.television',
    kicker: 'Television',
    title: 'Pilot + Bible Coverage',
    blurb: 'Pilot and series bible packs, plus limited-run series and TV pitch analysis.',
    fromLabel: '$39.99',
  },
  {
    href: '/pricing/theater',
    i18nKey: 'pricingHub.cards.theater',
    kicker: 'Theater',
    title: 'Stage Play Analysis',
    blurb: 'Theater Intelligence for live performance — production constraints, audience, and market tier.',
    fromLabel: '$29.99',
  },
  {
    href: '/pricing/additional-offerings',
    i18nKey: 'pricingHub.cards.additional',
    kicker: 'Additional Offerings',
    title: 'Novels, Decks & Territory',
    blurb: 'Novel adaptation, pitch deck review, and territorial censorship analysis.',
    fromLabel: 'From $9.99',
  },
  {
    href: '/pricing/authenticity',
    i18nKey: 'pricingHub.cards.authenticity',
    kicker: 'Add-ons',
    title: 'Quilty Authenticity',
    blurb: 'Optional AI-detection screening by format, plus Private Mode.',
    fromLabel: 'From +$2.00',
  },
  {
    href: '/discovery/membership',
    i18nKey: 'pricingHub.cards.discovery',
    kicker: 'Step 2',
    title: 'Discovery Network',
    blurb: 'Optional membership that puts scored scripts in front of verified buyers.',
    fromLabel: 'Three membership plans',
  },
  {
    href: '/enterprise',
    i18nKey: 'pricingHub.cards.enterprise',
    kicker: 'Teams',
    title: 'Enterprise Plans',
    blurb: 'Configure modules, seats, and credit blocks for studios, agencies, and production companies — CMS Mode included.',
    fromLabel: 'Build your configuration',
  },
  {
    href: '/ip-scout',
    kicker: 'IP Scout',
    title: 'IP Scout — Scout analysis',
    blurb: 'Adaptation intelligence for books, true stories, and other source IP. Scout tier is the purchasable analysis.',
    fromLabel: 'From $9.99',
  },
];
